import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon, Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost' | 'secondary' | 'solid';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', icon: Icon, loading, children, disabled, ...props }, ref) => {
    const variants = {
      primary: 'bg-foreground text-background hover:bg-foreground/90 font-medium active:scale-[0.98]',
      solid: 'bg-brand text-white hover:bg-brand-hover active:scale-[0.98]',
      secondary: 'bg-muted text-foreground hover:bg-muted/80 active:scale-[0.98]',
      outline: 'border border-border bg-transparent text-foreground hover:bg-muted/60 active:scale-[0.98]',
      ghost: 'text-muted-foreground hover:text-foreground hover:bg-muted/40 active:scale-[0.98]',
    };

    const sizes = {
      sm: 'h-8 px-3 text-xs tracking-wider uppercase font-semibold font-mono',
      md: 'h-10 px-5 text-sm font-medium',
      lg: 'h-12 px-7 text-base font-semibold',
    };

    return (
      <button
        ref={ref}
        data-variant={variant}
        disabled={disabled || loading}
        className={cn(
          'ui-button inline-flex items-center justify-center rounded-full transition-[background-color,color,border-color,box-shadow,transform] duration-200 cursor-pointer select-none',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
          variants[variant],
          sizes[size],
          (disabled || loading) && 'opacity-50 cursor-not-allowed pointer-events-none',
          className
        )}
        {...props}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {!loading && Icon && <Icon className="mr-2 h-4 w-4 shrink-0" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
